<script setup lang="ts">
    import { watch, ref } from 'vue';
    import { 
        AiDropdownOption,
        getHeadingTypes, 
        getLanguageOptions,
        getOrderedLanguageOptions, 
    } from '@cwd/AiTools/AiEditor.access';
    import Modal from '@shared/components/Modal.vue';
    import { setupShared } from '@cwd/AiTools/Shared';
    import Terms from '@cwd/AiTools/Terms.vue';
    import Loading from '@shared/components/Loading.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import { Language } from '@shared/Root/Languages.access';

    const { 
        shared,
        disabled,
        acceptTerms,
        showConsumption,
        getFormattedConsumption,
        updateConsumption,
        makeOptionsRequest,
        makeRequest,
    } = setupShared();

    const props = defineProps<{
        text: string;
        show: boolean;
    }>();

    watch(props, (newVal) => {
        if (props.show === true) init();
    });

    const emit = defineEmits(['update:text', 'close']);

    const languageOptions = ref<AiDropdownOption[]>([]);

    const init = async () => {
        shared.current = null;
        shared.resultOptions = [];

        await updateConsumption();

        let canvas = document.querySelector('.canvas') as HTMLElement;
        if (canvas != null) {
            shared.source = canvas.innerText.trim().substring(0, 10000);
        }

        if(languageOptions.value.length === 0){
            languageOptions.value = await getOrderedLanguageOptions();
        }
    };

    const insert = async () => {
        if (shared.selectedOption === null) return;
        emit('update:text', shared.resultOptions[shared.selectedOption]);
        close();
        shared.selectedOption = null;
    };

    const insertTranslatedSubject = (results: string[]) => {
        emit('update:text', results[0]);
        close();
    };

    const close = () => emit('close');
</script>

<script lang="ts"></script>

<template>
    <Modal :title="$Resources.AiAssistantCreateSurveyTitle" :beta="false" icon="ai" :show="props.show" @close="close" :scrollBody="false">
        <template #content>
            <Terms :termsAccepted="shared.termsAccepted" @accept="acceptTerms()"></Terms>

            <h3 class="h5">{{ $Resources.AIAssistantSubjectButton }}:</h3>

            <div class="form-group d-flex flex-wrap gap-sm btn-group-rounded">
                <button
                    v-for="headingType in getHeadingTypes()"
                    type="button"
                    class="btn btn-default"
                    @click="makeOptionsRequest(headingType.Command, shared.source, headingType.Parameters)"
                    :disabled="disabled()">
                    {{ headingType.Text }}
                </button>
            </div>

            <BSDropdownButton :closeOnClickInside="true" :disabled="disabled()">
                <template #toggle>
                    {{ $Resources.AIAssistantTranslate }}
                    <span class="caret m-xs-l"></span>
                </template>
                <template #content>
                    <li v-for="option in languageOptions">
                        <a href="javascript:void(0)" 
                        @click="makeRequest(option.Command, text, insertTranslatedSubject, option.Parameters)">
                            {{ option.Text }}
                        </a>
                    </li>
                </template>
            </BSDropdownButton>

            <h3 v-show="shared.resultOptions.length > 0" class="h5">{{ $Resources.AIAssistantSuggestions }}:</h3>

            <ul class="list-unstyled m-lg-b">
                <template v-for="(option, index) in shared.resultOptions">
                    <li>
                        <label class="btn btn-default btn-block m-xs-b text-left text-wrap" :class="{ 'active btn-primary': shared.selectedOption === index }">
                            <input type="radio" name="aiSubjects" class="sr-only" :value="index" v-model="shared.selectedOption" />
                            {{ index + 1 + '. ' + option }}
                        </label>
                    </li>
                </template>
            </ul>

            <div v-if="showConsumption()">
                <span>{{ getFormattedConsumption() }} % {{ $Resources.AIAssistantConsumptionPostText }}</span>
            </div>

            <Loading :show="shared.loading" :fixed="true"></Loading>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary p-lg-r p-lg-l" :disabled="disabled() || shared.selectedOption === null" @click="insert">
                {{ $Resources.AIAssistantInsertButton }}
            </button>
        </template>
    </Modal>
</template>

<style lang="less"></style>

<style scoped lang="less"></style>
